import React, { useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { SpaceCategory, SpaceCategoryLabels } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import useSessionStorage, { SessionStorageKeys } from "@hooks/useSessionStorage";
import { SpaceShuffleEvent } from "../../../AmplitudeSpaceEvents";
import { SpaceShuffleButtonSize } from "../../../SpaceSelectionConstants";
import SpaceCategoryShuffleButton from "./SpaceCategoryShuffleButton";
import ArrowLeftIcon from "/resources/icons/Arrow-Left-Icon.svg";

const SpaceCategoryRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    position: relative;
`;

const SpaceCategoryContainer = styled.div<{
    canScrollLeft: boolean;
    canScrollRight: boolean;
    hideScrollbar: boolean;
    isTouchDevice: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 10px;
    overflow-x: scroll;
    padding: 0 8px;
    position: relative;
    scroll-behavior: smooth;

    ${({ hideScrollbar, isTouchDevice }) =>
        !hideScrollbar &&
        css`
            ::-webkit-scrollbar-thumb {
                display: ${isTouchDevice ? `block` : `none`};
                border-radius: 8px;
                background-color: ${(props) => props.theme.controlPrimary};
                border: 2px ${(props) => props.theme.backgroundPrimary} solid;
            }
            ::-webkit-scrollbar-track {
                margin: 8px;
            }

            ::-webkit-scrollbar {
                display: block;
                height: 10px;
            }

            &:hover {
                ::-webkit-scrollbar-thumb,
                ::-webkit-scrollbar {
                    display: block;
                }
            }

            margin-top: 10px; //this is to account for the scrollbar on the bottom, so that the content is still centered
        `}

    ${({ isTouchDevice }) =>
        isTouchDevice &&
        css`
            margin: 10px 0;
        `}
`;

const RightArrowIcon = styled(ArrowLeftIcon)`
    fill: ${(props) => props.theme.iconColor};
    transform: rotate(180deg);
`;

const LeftArrowIcon = styled(ArrowLeftIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const DiscoverButton = styled(LifeAtButtonV2)`
    height: ${SpaceShuffleButtonSize}px;
    border-radius: 8px;
`;

const RightScrollButton = styled(LifeAtButtonV2)`
    position: absolute;
    right: 0;

    &:hover {
        background: ${(props) => props.theme.backgroundSecondaryTransparent};
    }
`;

const LeftScrollButton = styled(LifeAtButtonV2)`
    position: absolute;
    left: 0;

    &:hover {
        background: ${(props) => props.theme.backgroundSecondaryTransparent};
    }
`;

const SHUFFLING_DISABLED_TOOLTIP_LABEL = "Only host can shuffle spaces.";
const SCROLL_CAROUSEL_OFFSET = 150;
const DISCOVER_BUTTON_LABEL = "All";

interface SpaceShufflerProps {
    onDiscoverButtonClick: () => void;
    isDiscoverButtonActive: boolean;
    selectedSpaceCategory: SpaceCategory;
    selectCategory: (newCategory: SpaceCategory) => void;
}
export default function SpaceShuffler(props: SpaceShufflerProps) {
    const { userData }: UserContextType = useUserContext();
    const { spaceController } = useVirtualSpaceContext();

    const categoryRowRef = useRef(null);

    const amplitudeService = AmplitudeService.getInstance();

    const { roomId } = useParams<{ roomId?: string }>();
    const disableShuffler = !isRoomOwnedByUser(roomId, userData);

    const [hasLoggedSpaceShuffleEvent, setHasLoggedSpaceShuffleEvent] = useSessionStorage(
        SessionStorageKeys.HasLoggedSpaceShuffleEvent,
        false,
    );

    const logSpaceShuffleEvent = (spaceCategory: SpaceCategory, isChangingCategory: boolean) => {
        if (!hasLoggedSpaceShuffleEvent) {
            setHasLoggedSpaceShuffleEvent(true);
            amplitudeService.logEvent(new SpaceShuffleEvent(SpaceCategoryLabels[spaceCategory], !isChangingCategory));
        }
    };

    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(true);
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        setShowLeftScroll(categoryRowRef.current.scrollLeft > 0);
        setShowRightScroll(
            categoryRowRef.current.scrollLeft < categoryRowRef.current.scrollWidth - categoryRowRef.current.clientWidth,
        );
    };

    const scrollCarouselLeft = () => {
        categoryRowRef.current.scrollLeft -= SCROLL_CAROUSEL_OFFSET;
    };

    const scrollCarouselRight = () => {
        categoryRowRef.current.scrollLeft += SCROLL_CAROUSEL_OFFSET;
    };

    return (
        <SpaceCategoryRow>
            <SpaceCategoryContainer
                data-for={"space-shuffler-disabled-state-tooltip"}
                data-tip={SHUFFLING_DISABLED_TOOLTIP_LABEL}
                ref={categoryRowRef}
                onScroll={handleScroll}
                canScrollLeft={showLeftScroll}
                canScrollRight={showRightScroll}
                hideScrollbar={isMobile}
                isTouchDevice={isMobile || isTablet}
            >
                <DiscoverButton
                    size={"medium"}
                    onClick={props.onDiscoverButtonClick}
                    variant={props.isDiscoverButtonActive ? "secondary" : "ghost"}
                >
                    {DISCOVER_BUTTON_LABEL}
                </DiscoverButton>
                {spaceController.activeCategories.map((key, i) => {
                    const spaceCategory = SpaceCategory[key];
                    return (
                        <>
                            <SpaceCategoryShuffleButton
                                key={`space-shuffler-category-button-${spaceCategory}`}
                                spaceCategory={spaceCategory}
                                isActiveSpaceCategory={props.selectedSpaceCategory === spaceCategory}
                                selectCategory={() => props.selectCategory(spaceCategory)}
                                logSpaceShuffleEvent={() =>
                                    logSpaceShuffleEvent(
                                        spaceCategory,
                                        spaceController.selectedSpace.category !== spaceCategory,
                                    )
                                }
                                disabled={disableShuffler}
                            />
                        </>
                    );
                })}
            </SpaceCategoryContainer>
            {showLeftScroll && (
                <LeftScrollButton size={"small"} shape={"square"} onClick={scrollCarouselLeft} disableHover={true}>
                    <LeftArrowIcon />
                </LeftScrollButton>
            )}
            {showRightScroll && (
                <RightScrollButton size={"small"} shape={"square"} onClick={scrollCarouselRight} disableHover={true}>
                    <RightArrowIcon />
                </RightScrollButton>
            )}
        </SpaceCategoryRow>
    );
}
